// frontend/packages/business-console/src/components/build-interview/InterviewShell.jsx
//
// Shared header for every build-interview screen (Website Builder Redesign,
// Phase 1): a gradient sparkle tile + title + subtitle, a right-aligned
// "N of 10 done / about X min left" status, and a full-width gradient progress
// bar with a white dot riding the leading edge.
//
// Colors come from the console theme tokens (useTokens) so light/dark is
// automatic. Customer-visible copy is em-dash-free.

import React from 'react';
import { Box, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import useTokens from '../primitives/useTokens';

/** Gradient sparkle tile used across the interview (header, prompt bubble). */
export function SparkleTile({ size = 26, radius = 8 }) {
  const t = useTokens();
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        borderRadius: `${radius}px`,
        background: t.gradient,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: '0 4px 12px rgba(37,99,235,.30)',
      }}
    >
      <AutoAwesomeIcon sx={{ color: '#fff', fontSize: size * 0.6 }} />
    </Box>
  );
}

/** Full-width gradient progress bar with a dot on the leading edge. */
export function ProgressBar({ percent = 0 }) {
  const t = useTokens();
  const pct = Math.max(0, Math.min(100, Number(percent) || 0));
  return (
    <Box
      role="progressbar"
      aria-label="Interview progress"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuetext={`${Math.round(pct)} percent complete`}
      sx={{
        position: 'relative',
        height: 8,
        borderRadius: '5px',
        background: t.grayBorder,
        overflow: 'visible',
      }}
    >
      <Box
        sx={{
          height: '100%',
          width: `${pct}%`,
          borderRadius: '5px',
          background: t.gradient,
          transition: 'width .35s cubic-bezier(.2,.8,.2,1)',
        }}
      />
      {pct > 0 && pct < 100 ? (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: '50%',
            left: `${pct}%`,
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: '#fff',
            border: `3px solid ${t.purple}`,
            transform: 'translate(-50%, -50%)',
            transition: 'left .35s cubic-bezier(.2,.8,.2,1)',
          }}
        />
      ) : null}
    </Box>
  );
}

export default function InterviewShell({
  title = 'Build your website',
  subtitle = 'Answer a few questions and we build the first version of your site. Everything saves as you go.',
  doneCount = 0,
  totalSteps = 10,
  percent = 0,
  minutesLeft,
}) {
  const t = useTokens();
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <SparkleTile />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 900,
              letterSpacing: '-0.6px',
              lineHeight: 1.15,
              color: t.text,
            }}
          >
            {title}
          </Typography>
          <Typography sx={{ fontSize: 13, color: t.muted, mt: 0.25 }}>
            {subtitle}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0, display: { xs: 'none', sm: 'block' } }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: t.text }}>
            {doneCount} of {totalSteps} done
          </Typography>
          {Number.isFinite(minutesLeft) && minutesLeft > 0 ? (
            <Typography sx={{ fontSize: 11, color: t.faint }}>
              about {minutesLeft} min left
            </Typography>
          ) : null}
        </Box>
      </Box>
      <ProgressBar percent={percent} />
    </Box>
  );
}
